import { useContext, useEffect, useState, useMemo, useLayoutEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { appContext, AppContext } from '../../../context/AppContext';
import {
	Condition,
	Category,
	Century,
	Material,
	Product,
	ProductStatus,
	ProductToSet,
	validateProductToSet,
	Style,
	LocaleCode,
	Image,
	validateImage,
	validateImageList,
	NotFoundError,
	PRODUCT_IMAGE_MIN_WIDTH,
	PRODUCT_IMAGE_MIN_HEIGHT,
	LocalizedTexts,
	validateProductTextUpdate,
	ProductImage,
	Origin,
} from '@anticatrade/shared';
import {
	CategoriesResponse,
	CenturiesResponse,
	MaterialsResponse,
	ProductResponse,
	StylesResponse,
	ConditionsResponse,
	OriginsResponse,
} from '@anticatrade/admin';
import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 7000;

const useProduct = () => {
	const [retrievedProduct, setRetrievedProduct] = useState<Product>();
	const [productStatus, setProductStatus] = useState<ProductStatus>();
	const [productProcessingError, setProductProcessingError] = useState<string | null>();
	const [imageReprocessing, setImageReprocessing] = useState<boolean>(false);
	const [categories, setCategories] = useState<Category[]>();
	const [conditions, setConditions] = useState<Condition[]>();
	const [styles, setStyles] = useState<Style[]>();
	const [centuries, setCenturies] = useState<Century[]>();
	const [origins, setOrigins] = useState<Origin[]>();
	const [materials, setMaterials] = useState<Material[]>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [showTranslations, setShowTranslations] = useState<boolean>(false);
	const [productID, setProductID] = useState<string>();

	const { t, i18n } = useTranslation();
	const { api, handleError, redirect, merchant, refreshMerchant } = useContext(
		appContext as React.Context<AppContext>,
	);

	const params = useParams();
	const location = useLocation();

	const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();

	const isCreation = useMemo(() => {
		return location.pathname.endsWith('/add');
	}, [location]);

	useLayoutEffect(() => {
		setProductID(params.id == null ? undefined : params.id);
	}, [params]);

	const stopLoading = (err?: unknown) => {
		setIsLoading(false);
		if (err != null) {
			handleError(err as Error);
		}
	};

	useEffect(() => {
		if (!isCreation) {
			return;
		}

		const isSuspended = merchant!.status === 'suspended';
		if (isSuspended) {
			handleError(t('Account suspended message'));
			redirect('products');
			return;
		}

		let hasExpiredSubscription = false;
		let hasReachedSubscriptionLimit = false;
		hasExpiredSubscription = new Date(merchant!.subscriptionEnd!) < new Date();
		hasReachedSubscriptionLimit = merchant!.productUsed! >= merchant!.subscriptionLimit!;
		if (hasExpiredSubscription) {
			handleError(t('Subscription expired message'));
			redirect('products');
			return;
		}
		if (hasReachedSubscriptionLimit) {
			handleError(t('Subscription limit reached message'));
			redirect('products');
			return;
		}
	}, [productID, merchant]);

	useEffect(() => {
		const fetchData = async () => {
			if (!isCreation) {
				let productResponse: ProductResponse;
				try {
					productResponse = await api.product(Number(params.id));
				} catch (err) {
					if (err instanceof NotFoundError) {
						redirect('products');
						stopLoading(err);
						return;
					}
					stopLoading(err);
					return;
				}
				setRetrievedProduct(productResponse.product);
				setProductStatus(productResponse.product.status);
				setProductProcessingError(productResponse.product.processingError);
				setImageReprocessing(productResponse.product.imageReprocessing);
			}
			let categoriesResponse: CategoriesResponse;
			try {
				categoriesResponse = await api.categories();
			} catch (err) {
				stopLoading(err);
				return;
			}
			let conditionsResponse: ConditionsResponse;
			try {
				conditionsResponse = await api.conditions();
			} catch (err) {
				stopLoading(err);
				return;
			}
			let stylesResponse: StylesResponse;
			try {
				stylesResponse = await api.styles();
			} catch (err) {
				stopLoading(err);
				return;
			}
			let centuriesResponse: CenturiesResponse;
			try {
				centuriesResponse = await api.centuries();
			} catch (err) {
				stopLoading(err);
				return;
			}
			let originsResponse: OriginsResponse;
			try {
				originsResponse = await api.origins();
			} catch (err) {
				stopLoading(err);
				return;
			}
			let materialsResponse: MaterialsResponse;
			try {
				materialsResponse = await api.materials();
			} catch (err) {
				stopLoading(err);
				return;
			}
			setCategories(categoriesResponse.categories);
			setConditions(conditionsResponse.conditions);
			setStyles(stylesResponse.styles);
			setCenturies(centuriesResponse.centuries);
			setOrigins(originsResponse.origins);
			setMaterials(materialsResponse.materials);
			stopLoading();
		};
		setIsLoading(true);
		fetchData();
	}, [productID]);

	useEffect(() => {
		const shouldPoll =
			!isCreation && (productStatus === 'processing' || imageReprocessing);
		if (!shouldPoll) {
			if (pollIntervalRef.current != null) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = undefined;
			}
			return;
		}

		pollIntervalRef.current = setInterval(async () => {
			try {
				const res = await api.product(Number(productID));
				setProductStatus(res.product.status);
				setProductProcessingError(res.product.processingError);
				setImageReprocessing(res.product.imageReprocessing);
			} catch {
				// Ignore errors during polling.
			}
		}, POLL_INTERVAL_MS);

		return () => {
			if (pollIntervalRef.current != null) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = undefined;
			}
		};
	}, [productStatus, imageReprocessing, productID, isCreation]);

	const setProduct = async (
		productToSet: ProductToSet,
		image: Image | undefined,
		imageOriginal: Image | undefined,
		gallery: Image[] | undefined,
		originalGallery: ProductImage[] | null,
	): Promise<number | undefined> => {
		try {
			const localeCode = i18n.language as LocaleCode;
			validateProductToSet(productToSet, localeCode, !isCreation);
			await validateImage(
				image,
				localeCode,
				'image',
				PRODUCT_IMAGE_MIN_WIDTH,
				PRODUCT_IMAGE_MIN_HEIGHT,
				true,
				true,
				true,
			);
			await validateImageList(
				gallery,
				localeCode,
				'gallery',
				PRODUCT_IMAGE_MIN_WIDTH,
				PRODUCT_IMAGE_MIN_HEIGHT,
				0,
				12,
				false,
				true,
				true,
			);
		} catch (err) {
			throw err;
		}

		let id;
		try {
			if (isCreation) {
				const res = await api.addProduct(productToSet, image!, imageOriginal, gallery ?? []);
				id = res.id;
				refreshMerchant();
			} else {
				await api.updateProduct(
					Number(productID),
					productToSet,
					image!,
					imageOriginal,
					gallery ?? [],
					originalGallery!,
				);
			}
		} catch (err) {
			throw err;
		}
		return id;
	};

	const confirmProductTexts = async (
		title: LocalizedTexts,
		description: LocalizedTexts,
	): Promise<void> => {
		try {
			const localeCode = i18n.language as LocaleCode;
			validateProductTextUpdate(title, description, localeCode);
		} catch (err) {
			throw err;
		}

		let id;
		try {
			await api.updateProductTexts(Number(productID), title, description);
		} catch (err) {
			throw err;
		}
		return id;
	};

	useEffect(() => {
		const fetchProduct = async () => {
			let productResponse: ProductResponse;
			try {
				productResponse = await api.product(Number(productID));
			} catch (err) {
				stopLoading(err);
				return;
			}
			setRetrievedProduct(productResponse.product);
		};

		if (showTranslations) {
			fetchProduct();
		}
	}, [showTranslations]);

	const removeProduct = async () => {
		try {
			await api.deleteProduct(Number(productID));
		} catch (err) {
			throw err;
		}
	};

	return {
		productID,
		setProductID,
		setProduct,
		removeProduct,
		retrievedProduct,
		productStatus,
		productProcessingError,
		imageReprocessing,
		categories,
		conditions,
		styles,
		centuries,
		origins,
		materials,
		isLoading,
		setIsLoading,
		isCreation,
		showTranslations,
		setShowTranslations,
		confirmProductTexts,
	};
};

export { useProduct };
